import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Users } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationApi } from '../../services/notificationService';
import { useToastContext } from '../../contexts/ToastContext';

const AdminBroadcastModal = ({ isOpen, onClose, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [targetUserType, setTargetUserType] = useState('all');
  const [shouldClose, setShouldClose] = useState(false);
  const { showToast } = useToastContext();
  const queryClient = useQueryClient();
  const onCloseRef = useRef(onClose);

  // Keep ref updated
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const broadcastMutation = useMutation({
    mutationFn: (data) => notificationApi.createBroadcast(data),
    onSuccess: async (response) => {
      showToast(response.message || 'Broadcast sent successfully', 'success');
      // Invalidate and refetch all notification-related queries to refresh data
      await queryClient.invalidateQueries({ queryKey: ['notifications'], exact: false });
      await queryClient.invalidateQueries({ queryKey: ['adminNotifications'], exact: false });
      // Force refetch to ensure data is updated
      await queryClient.refetchQueries({ queryKey: ['adminNotifications'], exact: false });
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
      setShouldClose(true);
    },
    onError: (error) => {
      showToast(
        error?.response?.data?.message || 'Failed to send broadcast',
        'error'
      );
    },
  });

  // Close modal when shouldClose is true and mutation is done
  useEffect(() => {
    if (shouldClose && !broadcastMutation.isPending) {
      setTitle('');
      setMessage('');
      setTargetUserType('all');
      setShouldClose(false);
      onCloseRef.current();
    }
  }, [shouldClose, broadcastMutation.isPending]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTitle('');
      setMessage('');
      setTargetUserType('all');
      setShouldClose(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setTitle('');
    setMessage('');
    setTargetUserType('all');
    setShouldClose(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !message.trim()) {
      showToast('Please fill in all fields', 'error');
      return;
    }

    broadcastMutation.mutate({
      title: title.trim(),
      message: message.trim(),
      target_user_type: targetUserType,
    });
    setShouldClose(true);
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
              <Send className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Send Broadcast</h2>
              <p className="text-sm text-gray-500">Send a message to all users or specific user types</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            {/* Target User Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="w-4 h-4 inline mr-1" />
                Target Audience
              </label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setTargetUserType('all')}
                  className={`px-4 py-3 rounded-lg border-2 transition-all ${
                    targetUserType === 'all'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  <div className="font-medium">All Users</div>
                  <div className="text-xs text-gray-500 mt-1">Vendors & Clients</div>
                </button>
                <button
                  type="button"
                  onClick={() => setTargetUserType('vendor')}
                  className={`px-4 py-3 rounded-lg border-2 transition-all ${
                    targetUserType === 'vendor'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  <div className="font-medium">Vendors</div>
                  <div className="text-xs text-gray-500 mt-1">Vendors only</div>
                </button>
                <button
                  type="button"
                  onClick={() => setTargetUserType('client')}
                  className={`px-4 py-3 rounded-lg border-2 transition-all ${
                    targetUserType === 'client'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  <div className="font-medium">Clients</div>
                  <div className="text-xs text-gray-500 mt-1">Clients only</div>
                </button>
              </div>
            </div>

            {/* Title */}
            <div>
              <label htmlFor="broadcast-title" className="block text-sm font-medium text-gray-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="broadcast-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter broadcast title..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                maxLength={255}
                required
              />
              <p className="text-xs text-gray-500 mt-1">{title.length}/255 characters</p>
            </div>

            {/* Message */}
            <div>
              <label htmlFor="broadcast-message" className="block text-sm font-medium text-gray-700 mb-2">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                id="broadcast-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter broadcast message..."
                rows={6}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                maxLength={2000}
                required
              />
              <p className="text-xs text-gray-500 mt-1">{message.length}/2000 characters</p>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            disabled={broadcastMutation.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={broadcastMutation.isPending || !title.trim() || !message.trim()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {broadcastMutation.isPending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                <span>Send Broadcast</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminBroadcastModal;
